import { createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Form,
  InputGroup,
  Row,
} from "react-bootstrap";
import { Path, useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { RebalancesController } from "../../../api/src/rebalances/rebalances.controller";
import type {
  RebalanceRequest,
  RebalanceType,
} from "../../../api/src/rebalances/rebalances.service";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import ActionButton from "../components/ActionButton";
import Content from "../components/Content";
import FormFieldError from "../components/FormFieldError";
import HelpTooltip from "../components/HelpTooltip";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, useTable } from "../components/Table/Table";
import { useAuthenticatedMutationNew } from "../lib/api";
import { displayAccountName } from "../lib/display";
import { formatCurrency } from "../lib/numbers";
import CashOverrideModal, {
  type CashOverride,
  formatSignedCurrency,
} from "./CashOverrideModal";
import {
  type CashSettingsInput,
  formatCashMinMax,
  formatShare,
  summarizeCashTarget,
} from "./cashTarget";
import { rebalanceTypeDisplay } from "./lib";
import RaiseSpendCashWarning from "./RaiseSpendCashWarning";

const RebalanceTypeHelp = () => (
  <section className="text-start">
    <h5>Tolerance</h5>
    <p>
      Realign a household portfolio with their model (i.e. target) portfolio.
    </p>
    <h5>Raise Cash</h5>
    <p>Sell holdings to generate cash in an account.</p>
    <h5>Spend Cash</h5>
    <p>Use cash in an account to buy securities.</p>
    <h5>Manual</h5>
    <p>Enter individual trades manually.</p>
  </section>
);

type RebalanceRowFormState = Omit<
  RebalanceRequest,
  "householdId" | "householdName" | "type" | "trades" | "accounts" | "status"
> & {
  id: number;
  name: string;
  type: "household" | "account";
  balance: number;
  cashBalance: number;
  /**
   * The account's effective cash settings (`cashSettings` of
   * `GET /v1/accounts?balances=true`); `undefined` on a household row.
   */
  cashSettings?: CashSettingsInput | null;
  raiseSpendCashAmount?: number;
  accounts?: (RebalanceRowFormState & {
    type: "account";
    parentId: string;
  })[];
};

type RebalanceFormState = {
  households: (RebalanceRowFormState & {
    type: "household";
  })[];
};

export function useCreateRebalance() {
  return useAuthenticatedMutationNew<
    SerializedObject<UnpackResponse<RebalancesController["createRebalances"]>>,
    void,
    RebalanceRequest[]
  >({
    // APP-1652: served at `/v1` only; answers `{ data }` with the id and
    // status of each rebalance created.
    mutationKey: ["POST", "v1", "rebalances"],
    mutationFn: () => null,
  });
}

const CreateRebalance = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    control,
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<RebalanceFormState>();

  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds({ includeBalances: true });

  const {
    isPending: isPendingAccounts,
    isError: isErrorAccounts,
    data: dataAccounts,
  } = useQueryAllAccounts({
    includeBalances: true,
  });

  const householdsWithAccounts: RebalanceFormState | undefined = useMemo(() => {
    if (
      typeof dataHouseholds !== "undefined" &&
      typeof dataAccounts !== "undefined"
    ) {
      const accountsMap = _.groupBy(dataAccounts.data, "householdId");
      let households = dataHouseholds.data;

      // Filter to specific household if household_id is provided
      const householdIdParam = searchParams.get("household_id");
      if (householdIdParam) {
        const targetHouseholdId = parseInt(householdIdParam);
        households = households.filter(
          (household) => household.id === targetHouseholdId,
        );
      }

      return {
        households: households
          .filter(
            (household) =>
              household.assignedModelId != null ||
              (accountsMap[household.id] ?? []).some(
                (account) => account.assignedModelId != null,
              ),
          )
          .map((household) => ({
            id: household.id,
            name: household.name,
            type: "household",
            balance: household.householdBalance ?? 0,
            cashBalance: household.cashBalance ?? 0,
            accounts: (accountsMap[household.id] ?? [])
              .filter(
                (account) =>
                  household.assignedModelId != null ||
                  account.assignedModelId != null,
              )
              .map((account) => ({
                id: account.id,
                name: displayAccountName(
                  account.displayName,
                  account.displayNumber,
                ),
                type: "account",
                balance: account.accountBalance ?? 0,
                cashBalance: account.cashBalance ?? 0,
                cashSettings: account.cashSettings ?? null,
                parentId: `household-${household.id}`,
              })),
          })),
      };
    }
  }, [dataHouseholds, dataAccounts, searchParams]);

  useEffect(() => {
    if (typeof householdsWithAccounts !== "undefined") {
      reset(householdsWithAccounts);
    }
  }, [reset, householdsWithAccounts]);

  const [rebalanceType, setRebalanceType] = useState<RebalanceType | "">(() => {
    const typeParam = searchParams.get("type");
    return (typeParam as RebalanceType) || "";
  });
  const rebalanceTypeIsSet =
    typeof rebalanceType !== "undefined" && rebalanceType !== "";

  // Intraday cash adjustments, by account ID: the cash an account holds
  // right now, for the proposal being created only. A manual proposal never
  // reaches the rebalancer, and a TLH proposal does not use one, so neither
  // offers or sends one. An adjustment saved under another type stays here,
  // and Run names each account whose adjustment it did not send.
  const [cashOverrides, setCashOverrides] = useState<
    Record<number, CashOverride>
  >({});
  // The adjusted accounts the last Run left out of the request, which it
  // refused to send (Tolerance, Raise Cash and Spend Cash only).
  const [accountIdsWithOmittedAdjustment, setAccountIdsWithOmittedAdjustment] =
    useState<number[]>([]);
  const [adjustingAccount, setAdjustingAccount] = useState<{
    id: number;
    name: string;
    cashBalance: number;
  } | null>(null);
  const offersCashOverride =
    rebalanceTypeIsSet && rebalanceType !== "manual" && rebalanceType !== "tlh";
  // Account rows whose Raise/Spend Cash amount is positive, so clearing the
  // amount unchecks only a row that had one.
  const rowIdsWithAmount = useRef(new Set<string>());
  const isCashType =
    rebalanceType === "raise_cash" || rebalanceType === "spend_cash";

  const columnHelper = useMemo(
    () => createColumnHelper<RebalanceRowFormState>(),
    [],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        // Raise and Spend Cash are per account: no select-all and no
        // household checkbox, only each account's.
        header: (info) =>
          isCashType ? null : (
            <IndeterminateCheckbox
              checked={info.table.getIsAllRowsSelected()}
              indeterminate={info.table.getIsSomeRowsSelected()}
              onChange={info.table.getToggleAllPageRowsSelectedHandler()}
            />
          ),
        cell: (info) =>
          rebalanceType === "tolerance" || rebalanceType === "manual" ? null : (
            <IndeterminateCheckbox
              id={`select-${info.row.original.id}`}
              checked={info.row.getIsSelected()}
              indeterminate={info.row.getIsSomeSelected()}
              onChange={info.row.getToggleSelectedHandler()}
            />
          ),
        aggregatedCell: (info) =>
          isCashType ? null : (
            <IndeterminateCheckbox
              id={`select-${info.row.original.id}`}
              checked={info.row.getIsSelected()}
              indeterminate={info.row.getIsSomeSelected()}
              onChange={info.row.getToggleSelectedHandler()}
            />
          ),
      }),
      columnHelper.accessor((row) => row.name, {
        id: "name",
        cell: (info) => (
          <div
            style={{
              // Since rows are flattened by default,
              // we can use the row.depth property
              // and paddingLeft to visually indicate the depth
              // of the row
              paddingLeft: `${info.row.depth * 2}rem`,
            }}
          >
            <Link
              to={`/clients/${info.row.depth <= 0 ? "households" : "accounts"}/${info.row.original.id}`}
            >
              {info.getValue()}
            </Link>
          </div>
        ),
        aggregatedCell: (info) => (
          <>
            {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
            <Link
              to={`/clients/${info.row.depth <= 0 ? "households" : "accounts"}/${info.row.original.id}`}
            >
              {info.getValue()}
            </Link>
          </>
        ),
        header: () => "Household",
      }),
      columnHelper.accessor("balance", {
        cell: (info) => formatCurrency(info.getValue()),
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "AUM",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.cashBalance ?? 0, {
        id: "cashBalance",
        cell: (info) => {
          const reported = info.getValue();
          if (info.row.depth <= 0 || !offersCashOverride) {
            return formatCurrency(reported);
          }
          const { id, name } = info.row.original;
          const override = cashOverrides[id];
          const openAdjust = () =>
            setAdjustingAccount({ id, name, cashBalance: reported });
          if (override === undefined) {
            return (
              <>
                {formatCurrency(reported)}{" "}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline"
                  onClick={openAdjust}
                  aria-label={`Adjust cash for ${name}`}
                >
                  Adjust
                </Button>
              </>
            );
          }
          const adjusted = reported + override.adjustment;
          return (
            <div data-testid={`cash-override-${id}`}>
              <div>
                Cash adjusted{" "}
                <strong
                  className={
                    override.adjustment < 0 ? "text-danger" : "text-success"
                  }
                >
                  {formatSignedCurrency(override.adjustment)}
                </strong>{" "}
                <span className="text-muted">
                  ({formatCurrency(reported)} → {adjusted < 0 ? "−" : ""}
                  {formatCurrency(Math.abs(adjusted))})
                </span>{" "}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline"
                  onClick={openAdjust}
                  aria-label={`Edit cash adjustment for ${name}`}
                >
                  Edit
                </Button>{" "}
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 align-baseline text-danger"
                  onClick={() =>
                    setCashOverrides(({ [id]: _cleared, ...rest }) => rest)
                  }
                  aria-label={`Clear cash adjustment for ${name}`}
                >
                  Clear
                </Button>
              </div>
              <small className="text-muted">{override.reason}</small>
              {accountIdsWithOmittedAdjustment.includes(id) ? (
                <div>
                  <small className="text-danger">
                    {rebalanceType === "tolerance"
                      ? "Select the household of this account to use this adjustment."
                      : "Select this account to use this adjustment."}
                  </small>
                </div>
              ) : (
                (rebalanceType === "raise_cash" ||
                  rebalanceType === "spend_cash") &&
                !info.row.getIsSelected() && (
                  <div>
                    <small className="text-warning">
                      Select the account to apply the adjustment.
                    </small>
                  </div>
                )
              )}
            </div>
          );
        },
        aggregatedCell: (info) => formatCurrency(info.getValue()),
        header: () => "Cash",
        enableColumnFilter: false,
      }),
      columnHelper.display({
        id: "cashTarget",
        header: () => "Cash Target",
        cell: (info) => {
          if (info.row.original.type !== "account") return null;
          const target = summarizeCashTarget(
            info.row.original.cashSettings,
            info.row.original.balance,
          );
          if (target === null) {
            return <span className="text-muted">None</span>;
          }
          return (
            <div data-testid={`cash-target-${info.row.original.id}`}>
              <div>
                {formatCurrency(target.dollars)} ({formatShare(target.ratio)})
              </div>
              {target.split !== null && (
                <small className="text-muted">
                  {formatCurrency(target.split.sweepDollars)} sweep /{" "}
                  {formatCurrency(target.split.splitDollars)}{" "}
                  {target.split.symbol}
                </small>
              )}
            </div>
          );
        },
        aggregatedCell: () => null,
      }),
      columnHelper.display({
        id: "cashMinMax",
        header: () => "Min / Max",
        cell: (info) => {
          if (info.row.original.type !== "account") return null;
          const minMax = formatCashMinMax(info.row.original.cashSettings);
          return minMax === null ? (
            <span className="text-muted">None</span>
          ) : (
            <span data-testid={`cash-min-max-${info.row.original.id}`}>
              {minMax}
            </span>
          );
        },
        aggregatedCell: () => null,
      }),
      ...(rebalanceType === "raise_cash" || rebalanceType === "spend_cash"
        ? [
            columnHelper.accessor("raiseSpendCashAmount", {
              cell: (info) => {
                if (info.row.depth <= 0) return null;
                const field = register(
                  `households.${info.row.getParentRow()?.index}.accounts.${info.row.index}.raiseSpendCashAmount` as Path<RebalanceFormState>,
                );
                return (
                  <Form.Group>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        min="0"
                        max={
                          rebalanceType === "spend_cash"
                            ? // The adjusted cash, when the advisor entered
                              // one; nothing to spend below zero.
                              Math.max(
                                0,
                                info.row.original.cashBalance +
                                  (cashOverrides[info.row.original.id]
                                    ?.adjustment ?? 0),
                              )
                            : // The securities value, which an adjustment of
                              // the cash does not change.
                              info.row.original.balance -
                              info.row.original.cashBalance
                        }
                        aria-label={`${
                          rebalanceType === "spend_cash"
                            ? "Cash to Spend"
                            : "Cash to Raise"
                        } for ${info.row.original.name}`}
                        {...field}
                        onChange={(ev) => {
                          void field.onChange(ev);
                          // A positive amount means the account is meant to
                          // be in the proposal: check its row
                          // (`account-<id>`), which is what submit reads.
                          // Clearing an amount the row had unchecks it,
                          // however it was checked; a row checked before any
                          // amount is typed stays checked.
                          const amount = parseFloat(ev.target.value);
                          if (amount > 0) {
                            rowIdsWithAmount.current.add(info.row.id);
                            if (!info.row.getIsSelected()) {
                              info.row.toggleSelected(true);
                            }
                          } else if (
                            rowIdsWithAmount.current.has(info.row.id)
                          ) {
                            rowIdsWithAmount.current.delete(info.row.id);
                            if (info.row.getIsSelected()) {
                              info.row.toggleSelected(false);
                            }
                          }
                        }}
                      />
                    </InputGroup>
                    <FormFieldError
                      field={
                        errors.households?.[info.row.getParentRow()!.index]
                          ?.accounts?.[info.row.index]?.raiseSpendCashAmount
                      }
                    />
                    <RaiseSpendCashWarning
                      control={control}
                      name={
                        `households.${info.row.getParentRow()?.index}.accounts.${info.row.index}.raiseSpendCashAmount` as Path<RebalanceFormState>
                      }
                      type={rebalanceType}
                      effectiveCash={
                        info.row.original.cashBalance +
                        (cashOverrides[info.row.original.id]?.adjustment ?? 0)
                      }
                      adjusted={
                        cashOverrides[info.row.original.id] !== undefined
                      }
                    />
                  </Form.Group>
                );
              },
              header: () =>
                rebalanceType === "spend_cash"
                  ? "Cash to Spend"
                  : "Cash to Raise",
              enableColumnFilter: false,
            }),
          ]
        : []),
    ],
    [
      accountIdsWithOmittedAdjustment,
      cashOverrides,
      columnHelper,
      control,
      errors.households,
      isCashType,
      offersCashOverride,
      rebalanceType,
      register,
    ],
  );

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: householdsWithAccounts?.households ?? [],
    getRowId: (row) => `${row.type}-${row.id}`,
    getSubRows: (row) => row.accounts ?? [],
    initialState: {
      expanded: (() => {
        const householdIdParam = searchParams.get("household_id");
        const accountIdParam = searchParams.get("account_id");

        // Expand household if household_id or account_id is provided
        if (householdIdParam || accountIdParam) {
          const householdId = householdIdParam
            ? parseInt(householdIdParam)
            : null;
          if (householdId) {
            return { [`household-${householdId}`]: true };
          }
        }

        return {};
      })(),
    },
  });

  useEffect(() => {
    const householdIdParam = searchParams.get("household_id");
    const accountIdParam = searchParams.get("account_id");

    if (householdIdParam && householdsWithAccounts) {
      const householdId = parseInt(householdIdParam);
      const household = householdsWithAccounts.households.find(
        (h) => h.id === householdId,
      );

      if (household) {
        const selection: Record<string, boolean> = {
          [`household-${householdId}`]: true,
        };

        // If account_id is provided, also select that specific account
        if (accountIdParam) {
          const accountId = parseInt(accountIdParam);
          const account = household.accounts?.find((a) => a.id === accountId);
          if (account) {
            selection[`account-${accountId}`] = true;
          }
        }

        setRowSelection(selection);
      }
    }
  }, [householdsWithAccounts, searchParams, setRowSelection]);

  const createRebalance = useCreateRebalance();

  const notificationContext = useContext(NotificationContext);

  const onSubmit = useCallback(
    async (data: RebalanceFormState) => {
      setAccountIdsWithOmittedAdjustment([]);

      if (!rebalanceTypeIsSet) {
        console.error("RebalanceType not set");
        return;
      }

      // APP-1716: a second click while the create request is in flight must
      // not create a second proposal.
      if (createRebalance.isPending) {
        return;
      }

      const req = data.households
        .filter((household) =>
          rebalanceType === "tolerance" || rebalanceType === "manual"
            ? rowSelection[`household-${household.id}`]
            : (household.accounts ?? []).some(
                (account) => rowSelection[`account-${account.id}`],
              ),
        )
        .map((household): RebalanceRequest => ({
          ...household,
          householdId: household.id,
          householdName: household.name,
          type: rebalanceType,
          status: rebalanceType === "manual" ? "proposed" : "requested",
          accounts: (household.accounts ?? [])
            .filter(
              (account) =>
                rowSelection[`account-${account.id}`] ||
                // Every type but raise and spend cash trades the whole
                // household, so an adjusted account goes with it selected or
                // not; for those two the selected accounts are the ones the
                // proposal trades, and only theirs apply.
                (rebalanceType !== "raise_cash" &&
                  rebalanceType !== "spend_cash" &&
                  cashOverrides[account.id] !== undefined),
            )
            .map((account) => {
              const override = offersCashOverride
                ? cashOverrides[account.id]
                : undefined;
              return {
                id: account.id,
                accountName: account.name,
                raiseSpendCashAmount: parseFloat(
                  (account.raiseSpendCashAmount as string | undefined) ?? "0",
                ),
                ...(override === undefined
                  ? {}
                  : {
                      cashBalanceAdjustment: override.adjustment,
                      cashBalanceAdjustmentReason: override.reason,
                    }),
                trades: [],
              };
            }),
        }));

      // APP-1714: only ticked accounts go into the request. Refuse, before the
      // POST, an amount on an unticked account and a request with no account.
      let amountOnUntickedAccount = false;
      if (rebalanceType === "raise_cash" || rebalanceType === "spend_cash") {
        data.households.forEach((household, h) =>
          (household.accounts ?? []).forEach((account, a) => {
            if (
              !rowSelection[`account-${account.id}`] &&
              parseFloat(String(account.raiseSpendCashAmount ?? "")) > 0
            ) {
              amountOnUntickedAccount = true;
              setError(
                `households.${h}.accounts.${a}.raiseSpendCashAmount` as Path<RebalanceFormState>,
                { message: "Select this account to use this amount." },
              );
            }
          }),
        );
      }

      // R11/R12: an account with a saved adjustment that the request leaves
      // out would trade on its reported cash. Refuse the request and name each
      // such account on its row and in one notice. TLH and Manual send no
      // adjustment, so they are not checked.
      if (
        rebalanceType === "tolerance" ||
        rebalanceType === "raise_cash" ||
        rebalanceType === "spend_cash"
      ) {
        const requestedAccountIds = new Set(
          req.flatMap((household) =>
            household.accounts.map((account) => account.id),
          ),
        );
        const omittedAccounts = data.households.flatMap((household) =>
          (household.accounts ?? []).filter(
            (account) =>
              cashOverrides[account.id] !== undefined &&
              !requestedAccountIds.has(account.id),
          ),
        );
        if (omittedAccounts.length > 0) {
          setAccountIdsWithOmittedAdjustment(
            omittedAccounts.map((account) => account.id),
          );
          notificationContext.pushNotification({
            id: "rebalance",
            header: "Trade Proposal Not Created",
            body: `These accounts have a cash adjustment and are not in the trade proposal: ${omittedAccounts.map((account) => account.name).join(", ")}.`,
            variant: "danger",
          });
          return;
        }
      }

      if (rebalanceType === "raise_cash" || rebalanceType === "spend_cash") {
        if (req.length === 0) {
          notificationContext.pushNotification({
            id: "rebalance",
            header: "Trade Proposal Not Created",
            body: "Select at least one account.",
            variant: "danger",
          });
          return;
        }

        if (amountOnUntickedAccount) {
          return;
        }
      }

      try {
        await createRebalance.mutateAsync(req);

        notificationContext.pushNotification({
          id: "rebalance",
          header: "Trade Proposal Created",
          body: `${rebalanceTypeDisplay[rebalanceType]} trade proposal created`,
          variant: "success",
        });

        if (rebalanceType === "manual" || rebalanceType === "tlh") {
          const unusedAdjustmentNames = data.households.flatMap((household) =>
            (household.accounts ?? [])
              .filter((account) => cashOverrides[account.id] !== undefined)
              .map((account) => account.name),
          );
          if (unusedAdjustmentNames.length > 0) {
            notificationContext.pushNotification({
              id: "rebalance",
              header: "Cash Adjustment Not Used",
              body: `${rebalanceTypeDisplay[rebalanceType]} trade proposals do not use a cash adjustment. Not used for: ${unusedAdjustmentNames.join(", ")}.`,
              variant: "warning",
            });
          }
        }

        navigate("..");
      } catch (err) {
        console.error("Failed to create contact", err);
        notificationContext.pushNotification({
          id: "rebalance",
          header: "Failed to Create Trade Proposal",
          body: "Trade Proposal was not created",
          variant: "danger",
        });
      }
    },
    [
      cashOverrides,
      createRebalance,
      navigate,
      notificationContext,
      offersCashOverride,
      rebalanceType,
      rebalanceTypeIsSet,
      rowSelection,
      setError,
    ],
  );

  // The last Rebalance Type picked, from the `type` URL parameter or the
  // select. "Select..." is not a type and does not replace it.
  const lastRebalanceType = useRef<RebalanceType | null>(
    rebalanceType === "" ? null : rebalanceType,
  );

  const setRebalanceTypeHandler = useCallback(
    (ev: React.ChangeEvent<HTMLSelectElement>) => {
      const nextType = ev.target.value as RebalanceType | "";
      const isCash = (type: RebalanceType) =>
        type === "raise_cash" || type === "spend_cash";
      // Raise and Spend Cash have no select-all and no household checkbox, so
      // rows checked under another type would stay checked out of sight and
      // be submitted with a $0 amount. Uncheck every row when the type picked
      // is Raise or Spend Cash and the last type picked was not, or the
      // reverse. "Select..." between the two picks changes nothing: the rows
      // stay checked while the table is hidden. The first type picked unchecks
      // nothing, so a selection made from the URL with no `type` stays.
      if (nextType !== "") {
        const lastType = lastRebalanceType.current;
        if (lastType !== null && isCash(lastType) !== isCash(nextType)) {
          setRowSelection({});
          rowIdsWithAmount.current.clear();
        }
        lastRebalanceType.current = nextType;
      }
      setRebalanceType(nextType);
    },
    [setRowSelection],
  );

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col>
          <h1>Create Trade Proposal</h1>
        </Col>
        <Col md="auto">
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-4">
              <ActionButton
                label="Run Trade Proposal"
                icon="/icons/save.svg"
                type="submit"
                disabled={!rebalanceTypeIsSet || isSubmitting}
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                as={Link}
                to="/rebalances"
                label="Exit"
                icon="/icons/exit.svg"
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Content>
        <Row>
          <Col md={3} className="mb-3">
            <Form.Group controlId="formType">
              <Form.Label>Rebalance Type</Form.Label>
              <div className="d-flex">
                <Form.Select
                  placeholder="Select trade type"
                  value={rebalanceType}
                  onChange={setRebalanceTypeHandler}
                  className="me-1"
                >
                  <option value="">Select...</option>
                  {Object.entries(rebalanceTypeDisplay)
                    // A position swap's rebalances are created by the swap.
                    .filter(([key]) => key !== "position_swap")
                    .map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                </Form.Select>
                <HelpTooltip tooltip={<RebalanceTypeHelp />} />
              </div>
            </Form.Group>
          </Col>
        </Row>
        <CashOverrideModal
          show={adjustingAccount !== null}
          accountName={adjustingAccount?.name ?? ""}
          reportedCashBalance={adjustingAccount?.cashBalance ?? 0}
          rebalanceType={rebalanceType || undefined}
          value={
            adjustingAccount === null
              ? null
              : (cashOverrides[adjustingAccount.id] ?? null)
          }
          onSave={(value) => {
            if (adjustingAccount !== null) {
              const { id } = adjustingAccount;
              setCashOverrides((current) => ({ ...current, [id]: value }));
            }
            setAdjustingAccount(null);
          }}
          onClear={() => {
            if (adjustingAccount !== null) {
              const { id } = adjustingAccount;
              setCashOverrides(({ [id]: _cleared, ...rest }) => rest);
            }
            setAdjustingAccount(null);
          }}
          onHide={() => setAdjustingAccount(null)}
        />
        {rebalanceTypeIsSet && (
          <Row>
            <Col>
              {isPendingHouseholds || isPendingAccounts ? (
                <Loading />
              ) : isErrorHouseholds || isErrorAccounts ? (
                <Alert variant="danger">
                  An error occurred loading households and accounts
                </Alert>
              ) : (
                <Table table={table} />
              )}
            </Col>
          </Row>
        )}
      </Content>
    </Form>
  );
};

export default CreateRebalance;
